import { fetchConfig } from '@theme/utilities';
import { CartAddEvent } from '@theme/events';

class RedoBundlePicker extends HTMLElement {
  #abortController = new AbortController();
  #candleVariants = [];
  #refillProducts = [];
  #selectedSize = '';
  #submitting = false;

  connectedCallback() {
    this.#candleVariants = this.#parseData('[data-candle-variants]');
    this.#refillProducts = this.#parseData('[data-refill-products]');
    this.#selectedSize = this.dataset.initialSize || '';

    this.section = this.closest('.shopify-section');
    this.productForm = this.section?.querySelector('product-form-component');
    this.form = this.productForm?.querySelector('form');
    if (!this.form || !this.section || !this.productForm) return;

    const { signal } = this.#abortController;
    this.addEventListener('change', this.#handleChange, { signal });
    this.form.addEventListener('submit', this.#handleSubmit, { capture: true, signal });
    this.section.addEventListener('variant:update', this.#handleVariantUpdate, { signal });
    this.#setSize(this.#sizeForVariant(this.#candleVariantId()) || this.#selectedSize);
  }

  disconnectedCallback() {
    this.#abortController.abort();
  }

  #parseData(selector) {
    const element = this.querySelector(selector);
    if (!element?.textContent) return [];
    try {
      return JSON.parse(element.textContent);
    } catch (error) {
      console.error('Redo bundle picker data could not be read.', error);
      return [];
    }
  }

  #handleChange = (event) => {
    if (!(event.target instanceof HTMLInputElement)) return;
    if (event.target.name.startsWith('redo-format-')) {
      this.#syncFormat();
    } else if (event.target.matches('[data-refill-input]')) {
      this.#clearMessage();
      this.#syncSubmitButton();
    }
  };

  #handleVariantUpdate = (event) => {
    if (String(event.detail?.data?.productId) !== String(this.dataset.productId)) return;
    const variant = event.detail?.resource;
    if (!variant) return;

    const localVariant = this.#candleVariants.find((item) => String(item.id) === String(variant.id));
    if (localVariant) localVariant.available = variant.available;
    this.#setSize(this.#sizeForVariant(variant.id));
  };

  #handleSubmit = async (event) => {
    if (!this.#isBundleSelected()) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const candleVariantId = this.#candleVariantId();
    const refillVariantId = this.#selectedRefillVariantId();
    if (!candleVariantId || !refillVariantId) {
      this.#showMessage(`Choose an available ${this.#selectedSize || 'matching'} refill before adding this bundle.`);
      this.#syncSubmitButton();
      return;
    }

    this.#submitting = true;
    this.#clearMessage();
    this.#syncSubmitButton();

    const quantity = Math.max(1, Number.parseInt(this.form.querySelector('[name="quantity"]')?.value || '1', 10) || 1);
    const sections = Array.from(document.querySelectorAll('cart-items-component'))
      .map((component) => component.dataset.sectionId)
      .filter(Boolean)
      .join(',');
    const body = {
      items: [
        { id: candleVariantId, quantity, ...this.#candleFormData() },
        { id: refillVariantId, quantity },
      ],
      sections,
      sections_url: window.location.pathname,
    };

    try {
      const response = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body: JSON.stringify(body) }));
      const result = await response.json();
      if (!response.ok || result.status || result.errors) {
        throw new Error(result.description || result.message || 'The bundle could not be added to your cart.');
      }

      this.dispatchEvent(
        new CartAddEvent({}, String(candleVariantId), {
          source: 'redo-bundle-picker',
          itemCount: quantity * 2,
          productId: this.dataset.productId,
          sections: result.sections,
        })
      );
    } catch (error) {
      this.#showMessage(error instanceof Error ? error.message : 'The bundle could not be added to your cart.');
    } finally {
      this.#submitting = false;
      this.#syncSubmitButton();
    }
  };

  #candleFormData() {
    const data = new FormData(this.form);
    const properties = {};
    for (const [name, value] of data.entries()) {
      const match = /^properties\[(.+)\]$/.exec(name);
      if (match && typeof value === 'string') properties[match[1]] = value;
    }
    const sellingPlan = data.get('selling_plan');
    return {
      ...(Object.keys(properties).length ? { properties } : {}),
      ...(typeof sellingPlan === 'string' && sellingPlan ? { selling_plan: sellingPlan } : {}),
    };
  }

  #syncFormat() {
    const panel = this.querySelector('[data-refill-panel]');
    if (panel) panel.hidden = !this.#isBundleSelected();
    if (this.#isBundleSelected() && !this.#selectedRefillVariantId()) {
      this.#showMessage(`No ${this.#selectedSize || 'matching'} refill is currently available.`);
    } else {
      this.#clearMessage();
    }
    this.#syncAcceleratedCheckout();
    this.#syncSubmitButton();
  }

  #setSize(size) {
    this.#selectedSize = size || '';
    this.querySelectorAll('[data-size-label]').forEach((label) => {
      label.textContent = this.#selectedSize || 'Unavailable';
    });

    let selectedInput = this.querySelector('[data-refill-input]:checked');
    let selectedIsAvailable = false;
    this.querySelectorAll('[data-refill-option]').forEach((option) => {
      const input = option.querySelector('[data-refill-input]');
      const status = option.querySelector('[data-refill-status]');
      const product = this.#refillProducts.find((item) => String(item.id) === option.dataset.productId);
      const matchingVariant = product?.variants?.find(
        (variant) => variant.size === this.#selectedSize && variant.available
      );
      if (!input) return;

      input.disabled = !matchingVariant;
      option.classList.toggle('is-unavailable', !matchingVariant);
      option.dataset.variantId = matchingVariant?.id || '';
      if (status) status.textContent = matchingVariant ? `${this.#selectedSize} available` : `Not available in ${this.#selectedSize || 'this size'}`;
      if (input.checked && matchingVariant) selectedIsAvailable = true;
    });

    if (!selectedIsAvailable) {
      selectedInput = Array.from(this.querySelectorAll('[data-refill-input]')).find((input) => !input.disabled);
      if (selectedInput) selectedInput.checked = true;
    }

    if (this.#isBundleSelected() && !this.#selectedRefillVariantId()) {
      this.#showMessage(`No ${this.#selectedSize || 'matching'} refill is currently available.`);
    } else {
      this.#clearMessage();
    }
    this.#syncAcceleratedCheckout();
    this.#syncSubmitButton();
  }

  #isBundleSelected() {
    return this.querySelector('[name^="redo-format-"][value="bundle"]')?.checked === true;
  }

  #candleVariantId() {
    return this.form?.querySelector('input[name="id"]')?.value || '';
  }

  #sizeForVariant(id) {
    return this.#candleVariants.find((variant) => String(variant.id) === String(id))?.size || '';
  }

  #selectedRefillVariantId() {
    const selected = this.querySelector('[data-refill-input]:checked');
    if (!(selected instanceof HTMLInputElement) || selected.disabled) return '';
    return selected.closest('[data-refill-option]')?.dataset.variantId || '';
  }

  #candleAvailable() {
    return this.#candleVariants.find((variant) => String(variant.id) === String(this.#candleVariantId()))?.available !== false;
  }

  #syncSubmitButton() {
    const button = this.form?.querySelector('[ref="addToCartButton"]');
    if (!(button instanceof HTMLButtonElement)) return;
    button.disabled = !this.#candleAvailable() || (this.#isBundleSelected() && (!this.#selectedRefillVariantId() || this.#submitting));
    button.setAttribute('aria-busy', this.#submitting ? 'true' : 'false');
  }

  #syncAcceleratedCheckout() {
    const acceleratedCheckout = this.productForm?.querySelector('[ref="acceleratedCheckoutButtonContainer"]');
    if (!acceleratedCheckout) return;
    if (this.#isBundleSelected()) acceleratedCheckout.setAttribute('hidden', 'true');
    else if (this.#candleAvailable()) acceleratedCheckout.removeAttribute('hidden');
  }

  #showMessage(message) {
    const target = this.querySelector('[data-message]');
    if (!target) return;
    target.textContent = message;
    target.hidden = false;
  }

  #clearMessage() {
    const target = this.querySelector('[data-message]');
    if (!target) return;
    target.textContent = '';
    target.hidden = true;
  }
}

if (!customElements.get('redo-bundle-picker')) {
  customElements.define('redo-bundle-picker', RedoBundlePicker);
}
